"use client";

import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Strategy() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const drafts = [{
    batch: "Phase 01",
    abv: "Audit",
    name: "Market Analysis",
    descriptors: "Competitor · Keywords · Gaps",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_market_analysis.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_market_analysis.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_market_analysis.webp-wvc-sizes",
    tint: "hsl(222 47% 12% / 0.05)",
    colSpan: "lg:col-start-1 lg:col-end-6",
    offset: "lg:mt-0",
    aspect: "aspect-[4/5]"
  }, {
    batch: "Phase 02",
    abv: "Content",
    name: "Authority Building",
    descriptors: "Backlinks · Outreach · Trust",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_authority_building.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_authority_building.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_authority_building.png-wvc-sizes",
    tint: "hsl(43 46% 55% / 0.08)",
    colSpan: "lg:col-start-7 lg:col-end-11",
    offset: "lg:mt-32",
    aspect: "aspect-[3/4]"
  }, {
    batch: "Phase 03",
    abv: "Ads",
    name: "Paid Acquisition",
    descriptors: "PPC · Retargeting · ROI",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_paid_acquisition.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_paid_acquisition.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_paid_acquisition.webp-wvc-sizes",
    tint: "hsl(222 47% 12% / 0.05)",
    colSpan: "lg:col-start-2 lg:col-end-7",
    offset: "lg:mt-20",
    aspect: "aspect-[4/5]"
  }, {
    batch: "Phase 04",
    abv: "Tech",
    name: "Technical SEO",
    descriptors: "Speed · Schema · Crawl",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_technical_optimization.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_technical_optimization.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_technical_optimization.png-wvc-sizes",
    tint: "hsl(43 46% 55% / 0.08)",
    colSpan: "lg:col-start-8 lg:col-end-13",
    offset: "lg:-mt-16",
    aspect: "aspect-[3/4]"
  }, {
    batch: "Phase 05",
    abv: "Data",
    name: "Conversion Tracking",
    descriptors: "Analytics · Attribution · UX",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_conversion_tracking.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_conversion_tracking.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_conversion_tracking.png-wvc-sizes",
    tint: "hsl(222 47% 12% / 0.05)",
    colSpan: "lg:col-start-1 lg:col-end-5",
    offset: "lg:mt-24",
    aspect: "aspect-[4/5]"
  }, {
    batch: "Phase 06",
    abv: "Scale",
    name: "Growth Scaling",
    descriptors: "Automation · Expansion · Profit",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_growth_scaling.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_growth_scaling.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/seo_growth_scaling.png-wvc-sizes",
    tint: "hsl(43 46% 55% / 0.08)",
    colSpan: "lg:col-start-7 lg:col-end-12",
    offset: "lg:mt-8",
    aspect: "aspect-[3/4]"
  }];
  return <section data-section-id="5292" id="seo-ads-strategy" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="lg:col-span-5 flex flex-col justify-between">
          <span className="font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-6 block">
            Strategic Framework — Growth Services
          </span>
          <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-[60px] leading-[1.05] text-foreground tracking-tight">
            Our proven{" "}
            <em className="italic font-normal text-secondary">strategy</em>
          </h2>
        </div>

        <div className="lg:col-span-7 lg:pl-12 flex flex-col justify-end">
          <p className="font-default text-base md:text-lg text-foreground leading-relaxed max-w-xl mb-6">
            Six core pillars of digital growth designed to maximize visibility, 
            drive qualified traffic, and convert visitors into loyal customers. 
            Data-driven precision meets creative execution.
          </p>
          <div className="flex justify-end">
            <Link to="#" className="group inline-flex items-center gap-2 font-default text-[12px] uppercase tracking-[0.16em] text-foreground hover:text-secondary transition-colors duration-300">
              View All Services
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-y-16 gap-x-6 lg:gap-x-8 relative">
        {drafts.map((draft, i) => {
        const {
          ref,
          isVisible
        } = useRevealOnScroll<HTMLDivElement>({
          threshold: 0.15
        });
        return <div data-index={i} key={draft.name} ref={ref} className={`${draft.colSpan} ${draft.offset} group transition-all duration-1000 ease-out`} style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(40px)",
          transitionDelay: `${i % 3 * 100}ms`
        }}>
              <article className="relative rounded-lg p-5 md:p-6 transition-all duration-700 ease-out group-hover:-translate-y-1.5 shadow-md" style={{
            backgroundColor: draft.tint
          }}>
                <div className={`relative overflow-hidden rounded-md ${draft.aspect} bg-muted`}>
                  <img src={draft.image} data-wvc-srcset={draft.srcset} data-wvc-sizes={draft.sizes} alt={draft.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                </div>

                <div className="mt-6 flex items-center gap-2 font-default text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  <span className="tabular-nums">{draft.batch}</span>
                  <span aria-hidden="true">·</span>
                  <span className="tabular-nums">{draft.abv}</span>
                </div>

                <h3 className="mt-3 font-default font-bold text-2xl md:text-[32px] leading-tight text-foreground relative inline-block">
                  <span className="relative">
                    {draft.name}
                    <span className="absolute left-0 -bottom-1 h-px bg-foreground w-0 group-hover:w-full transition-all duration-500 ease-out" />
                  </span>
                </h3>

                <p className="mt-2 font-default italic text-sm text-muted-foreground">
                  {draft.descriptors}
                </p>

                <div className="mt-6 flex justify-end">
                  <Button size="icon" variant="default" className="rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/90 size-10 transition-transform duration-300 group-hover:rotate-[-12deg]" aria-label={`Read more about ${draft.name}`}>
                    <ArrowUpRight className="size-4" />
                  </Button>
                </div>
              </article>
            </div>;
      })}
      </div>

      <div className="hidden lg:block absolute bottom-8 right-8 opacity-20 pointer-events-none">
        <svg width="180" height="220" viewBox="0 0 180 220" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M90 10 C 90 30, 70 40, 60 50 C 50 60, 50 75, 60 85 C 70 75, 85 80, 90 95 C 95 80, 110 75, 120 85 C 130 75, 130 60, 120 50 C 110 40, 90 30, 90 10 Z" stroke="currentColor" strokeWidth="1.2" fill="none" />
          <path d="M90 95 C 90 115, 70 125, 60 135 C 50 145, 50 160, 60 170 C 70 160, 85 165, 90 180 C 95 165, 110 160, 120 170 C 130 160, 130 145, 120 135 C 110 125, 90 115, 90 95 Z" stroke="currentColor" strokeWidth="1.2" fill="none" />
          <path d="M90 95 L 90 10 M 90 180 L 90 95 M 90 180 L 90 215" stroke="currentColor" strokeWidth="1" />
          <path d="M90 200 Q 75 205, 65 215 M 90 200 Q 105 205, 115 215" stroke="currentColor" strokeWidth="1" fill="none" />
        </svg>
      </div>
    </section>;
}